import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { buscarIW, fmtExportacao } from './iw/apiIW';

// Visitas × Produtividade — agendas NÃO realizadas do IW (TDEXP_VISITASNR, gerado toda madrugada).
// Cada linha do IW é uma agenda (especialidade, frequência e período) que não foi realizada.
const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#475569', primary: '#4f46e5', primaryDark: '#3730a3', primarySoft: '#eef2ff' };
const UNIDADES = ['Todas', 'VitalCare', 'HomeBaby'];
const fmtData = iso => (iso ? iso.split('-').reverse().join('/') : '—');

function agrupar(lista, chave) {
  const m = new Map();
  lista.forEach(v => { const k = chave(v) || '(não informado)'; m.set(k, (m.get(k) || 0) + 1); });
  return [...m.entries()].map(([nome, qtd]) => ({ nome, qtd })).sort((a, b) => b.qtd - a.qtd);
}

function Barras({ titulo, itens, total, selecionado, onSelecionar, max = 12 }) {
  const maior = Math.max(1, ...itens.map(i => i.qtd));
  return (
    <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 16, padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontWeight: 800 }}>{titulo} <span style={{ fontWeight: 500, fontSize: '0.76rem', color: P.muted }}>· clique para filtrar</span></div>
      {itens.slice(0, max).map(i => (
        <button key={i.nome} onClick={() => onSelecionar(selecionado === i.nome ? null : i.nome)}
          style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 190px) minmax(0,1fr) 54px', gap: 10, alignItems: 'center', fontSize: '0.82rem', border: 0, borderRadius: 8, padding: '4px 6px', cursor: 'pointer', textAlign: 'left',
            background: selecionado === i.nome ? P.primarySoft : 'transparent' }}>
          <span style={{ fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.nome}>{i.nome}</span>
          <span style={{ height: 10, borderRadius: 5, background: '#f1f5f9', overflow: 'hidden' }}>
            <span style={{ display: 'block', height: 10, borderRadius: 5, background: P.primary, width: `${Math.round(i.qtd / maior * 100)}%` }} />
          </span>
          <span style={{ fontWeight: 800, textAlign: 'right' }}>{i.qtd}<span style={{ fontWeight: 500, color: P.muted, fontSize: '0.7rem' }}> {total ? Math.round(i.qtd / total * 100) : 0}%</span></span>
        </button>
      ))}
      {itens.length > max && <div style={{ fontSize: '0.74rem', color: P.muted }}>+ {itens.length - max} outros</div>}
    </div>
  );
}

export default function GestaoVisitasProdutividade() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [unidade, setUnidade] = useState('Todas');
  const [soAtivos, setSoAtivos] = useState(true);
  const [esp, setEsp] = useState(null);
  const [prestador, setPrestador] = useState(null);
  const [busca, setBusca] = useState('');

  const carregar = async () => {
    setCarregando(true); setErro('');
    try { setDados(await buscarIW('iw-visitas')); } catch (e) { setErro(e.message); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);

  const base = useMemo(() => (dados?.visitas || []).filter(v =>
    (unidade === 'Todas' || v.unidade === unidade) && (!soAtivos || /atendimento/i.test(v.status))), [dados, unidade, soAtivos]);
  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return base.filter(v => (!esp || v.especialidade === esp) && (!prestador || (v.prestador || '(não informado)') === prestador)
      && (!q || (v.paciente || '').toLowerCase().includes(q) || String(v.numIW).includes(q)));
  }, [base, esp, prestador, busca]);

  const porEspecialidade = useMemo(() => agrupar(base.filter(v => !prestador || (v.prestador || '(não informado)') === prestador), v => v.especialidade), [base, prestador]);
  const porPrestador = useMemo(() => agrupar(base.filter(v => !esp || v.especialidade === esp), v => v.prestador), [base, esp]);
  const porPaciente = useMemo(() => {
    const m = new Map();
    filtradas.forEach(v => {
      const k = v.numIW;
      if (!m.has(k)) m.set(k, { numIW: k, paciente: v.paciente, unidade: v.unidade, qtd: 0, especialidades: new Set() });
      const x = m.get(k); x.qtd++; x.especialidades.add(v.especialidade);
    });
    return [...m.values()].sort((a, b) => b.qtd - a.qtd);
  }, [filtradas]);
  const contagemUnidade = useMemo(() => {
    const c = { Todas: 0, VitalCare: 0, HomeBaby: 0 };
    (dados?.visitas || []).forEach(v => { if (!soAtivos || /atendimento/i.test(v.status)) { c.Todas++; if (c[v.unidade] !== undefined) c[v.unidade]++; } });
    return c;
  }, [dados, soAtivos]);

  const pacientesDistintos = new Set(filtradas.map(v => v.numIW)).size;

  return (
    <div style={{ padding: '24px 28px', background: P.bg, minHeight: '100%', color: P.text, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: P.primaryDark }}>Dashboard</div>
          <h1 style={{ margin: '4px 0', fontSize: '1.6rem', fontWeight: 800 }}>Visitas × Produtividade</h1>
          <div style={{ fontSize: '0.86rem', color: P.muted }}>Agendas não realizadas · dados do IW · exportação de {fmtExportacao(dados?.atualizadoEm)} (atualiza sozinho toda madrugada)</div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <label style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: -9999 }}>Buscar paciente ou nº IW</span>
            <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: '#94a3b8' }} />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar paciente ou nº IW"
              style={{ padding: '9px 12px 9px 32px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: '0.86rem', width: 220 }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: P.muted }}>
            <input type="checkbox" checked={soAtivos} onChange={e => setSoAtivos(e.target.checked)} /> Só pacientes em atendimento
          </label>
          <div style={{ display: 'flex', gap: 4, background: '#e2e8f0', padding: 4, borderRadius: 10 }}>
            {UNIDADES.map(u => (
              <button key={u} onClick={() => setUnidade(u)}
                style={{ border: 0, borderRadius: 8, padding: '7px 13px', fontSize: '0.8rem', fontWeight: unidade === u ? 800 : 600, cursor: 'pointer', background: unidade === u ? P.white : 'transparent', color: P.text }}>
                {u} · {contagemUnidade[u] ?? 0}
              </button>
            ))}
          </div>
          <button onClick={carregar} title="Recarregar" aria-label="Recarregar" style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 10, padding: 9, cursor: 'pointer', display: 'flex' }}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {erro && <div style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 10, padding: '10px 14px', fontSize: '0.86rem' }}>Não foi possível carregar: {erro}</div>}
      {carregando && <div style={{ color: P.muted }}>Carregando dados do IW…</div>}

      {!carregando && !erro && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {[
              ['Agendas não realizadas', filtradas.length, esp || prestador ? 'com o filtro selecionado' : 'no recorte atual'],
              ['Pacientes afetados', pacientesDistintos, 'com pelo menos uma agenda não realizada'],
              ['Especialidade com mais pendências', porEspecialidade[0]?.nome || '—', porEspecialidade[0] ? `${porEspecialidade[0].qtd} agendas` : ''],
              ['Média por paciente', pacientesDistintos ? (filtradas.length / pacientesDistintos).toFixed(1) : '0', 'agendas não realizadas'],
            ].map(([t, v, d]) => (
              <div key={t} style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: P.muted }}>{t}</span>
                <span style={{ fontSize: typeof v === 'string' && v.length > 10 ? '1.1rem' : '1.8rem', fontWeight: 800 }}>{v}</span>
                <span style={{ fontSize: '0.74rem', color: '#64748b' }}>{d}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
            <Barras titulo="Por especialidade" itens={porEspecialidade} total={porEspecialidade.reduce((s, i) => s + i.qtd, 0)} selecionado={esp} onSelecionar={setEsp} />
            <Barras titulo="Por prestador / equipe" itens={porPrestador} total={porPrestador.reduce((s, i) => s + i.qtd, 0)} selecionado={prestador} onSelecionar={setPrestador} />
          </div>

          <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.border}`, fontWeight: 800 }}>
              Pacientes com mais agendas não realizadas
              {(esp || prestador) && <span style={{ fontWeight: 600, color: P.muted }}> · {[esp, prestador].filter(Boolean).join(' · ')} <button onClick={() => { setEsp(null); setPrestador(null); }} style={{ border: 0, background: 'none', color: '#4338ca', cursor: 'pointer', fontWeight: 700 }}>limpar</button></span>}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', minWidth: 700 }}>
                <thead>
                  <tr style={{ background: P.bg, textAlign: 'left', color: P.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    {['Paciente', 'Nº IW', 'Unidade', 'Agendas não realizadas', 'Especialidades'].map(h => <th key={h} style={{ padding: '11px 14px', fontWeight: 700 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {porPaciente.slice(0, 50).map(p => (
                    <tr key={p.numIW} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '11px 14px', fontWeight: 700 }}>{p.paciente}</td>
                      <td style={{ padding: '11px 14px', color: P.muted }}>{p.numIW}</td>
                      <td style={{ padding: '11px 14px' }}>{p.unidade}</td>
                      <td style={{ padding: '11px 14px', fontWeight: 800, color: p.qtd >= 10 ? '#9a3412' : P.text }}>{p.qtd}</td>
                      <td style={{ padding: '11px 14px', color: P.muted, fontSize: '0.8rem' }}>{[...p.especialidades].join(', ')}</td>
                    </tr>
                  ))}
                  {porPaciente.length === 0 && <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Nenhuma agenda não realizada com esse filtro.</td></tr>}
                </tbody>
              </table>
            </div>
            {porPaciente.length > 50 && <div style={{ padding: '10px 18px', borderTop: `1px solid ${P.border}`, fontSize: '0.8rem', color: P.muted }}>Mostrando os 50 pacientes com mais pendências de {porPaciente.length}.</div>}
          </div>

          <div style={{ fontSize: '0.78rem', color: P.muted }}>
            Período das agendas no arquivo: {fmtData((dados?.visitas || []).reduce((m, v) => (!m || (v.inicio && v.inicio < m) ? v.inicio : m), ''))} a {fmtData((dados?.visitas || []).reduce((m, v) => (v.fim > m ? v.fim : m), ''))}.
            O IW exporta só as agendas não realizadas; para medir também as realizadas é preciso a IW exportar essas visitas.
          </div>
        </>
      )}
    </div>
  );
}
